import { useCallback, useEffect, useMemo, useRef, useState, type UIEvent } from 'react'
import { toast } from 'sonner'

const API_BASE = import.meta.env.VITE_API_BASE_URL || ''

type AssignmentRow = {
  user_id: string
  email: string | null
  username: string | null
}

type Candidate = {
  id: string
  username: string | null
  email: string | null
  already_assigned: boolean
}

function initialsFromUser(u: { username?: string | null; email?: string | null }): string {
  const s = (u.username || u.email || '?').trim()
  const parts = s.split(/[\s@._-]+/).filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase().slice(0, 2)
  return s.slice(0, 2).toUpperCase()
}

const AV = ['#a78bfa', '#6ee7b7', '#fdba74', '#93c5fd', '#f9a8d4', '#fcd34d']

/** First page size when opening / changing search; each scroll loads this many more. */
const CANDIDATES_INITIAL_LIMIT = 15
const CANDIDATES_PAGE_MORE = 10

type AssignUsersDrawerProps = {
  open: boolean
  skillId: string
  skillName: string
  onClose: () => void
  onSaved: () => void
}

export function AssignUsersDrawer({ open, skillId, skillName, onClose, onSaved }: AssignUsersDrawerProps) {
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [assigned, setAssigned] = useState<AssignmentRow[]>([])
  const [candidates, setCandidates] = useState<Candidate[]>([])
  const [candidatesHasMore, setCandidatesHasMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loadingCand, setLoadingCand] = useState(false)
  const [loadingMoreCand, setLoadingMoreCand] = useState(false)
  const candidatesRef = useRef<Candidate[]>([])
  useEffect(() => {
    candidatesRef.current = candidates
  }, [candidates])
  const [saving, setSaving] = useState(false)
  const [revoking, setRevoking] = useState<Set<string>>(new Set())
  const [adding, setAdding] = useState<Set<string>>(new Set())

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 280)
    return () => clearTimeout(t)
  }, [search])

  const loadAssigned = useCallback(async () => {
    if (!skillId) return
    setLoading(true)
    try {
      const res = await fetch(`${API_BASE}api/skills/${encodeURIComponent(skillId)}/assignments`, { credentials: 'include' })
      if (!res.ok) throw new Error('Failed to load assignments')
      const data = (await res.json()) as { assignments?: AssignmentRow[] }
      setAssigned(Array.isArray(data.assignments) ? data.assignments : [])
    } catch {
      toast.error('Could not load assignments')
      setAssigned([])
    } finally {
      setLoading(false)
    }
  }, [skillId])

  const fetchCandidatesPage = useCallback(
    async (offset: number, limit: number, append: boolean) => {
      if (!skillId) return
      if (append) {
        setLoadingMoreCand(true)
      } else {
        setLoadingCand(true)
      }
      try {
        const q = new URLSearchParams()
        q.set('offset', String(offset))
        q.set('limit', String(limit))
        if (debounced) q.set('q', debounced)
        const res = await fetch(
          `${API_BASE}api/skills/${encodeURIComponent(skillId)}/assign-candidates?${q.toString()}`,
          { credentials: 'include' }
        )
        if (!res.ok) throw new Error('Search failed')
        const data = (await res.json()) as { users?: Candidate[]; has_more?: boolean }
        const users = Array.isArray(data.users) ? data.users : []
        const hasMore = data.has_more === true
        if (append) {
          setCandidates(prev => {
            const seen = new Set(prev.map(u => u.id))
            const merged = [...prev]
            for (const u of users) {
              if (!seen.has(u.id)) {
                seen.add(u.id)
                merged.push(u)
              }
            }
            return merged
          })
        } else {
          setCandidates(users)
        }
        setCandidatesHasMore(hasMore)
      } catch {
        if (!append) {
          setCandidates([])
          setCandidatesHasMore(false)
        }
      } finally {
        if (append) {
          setLoadingMoreCand(false)
        } else {
          setLoadingCand(false)
        }
      }
    },
    [skillId, debounced]
  )

  const loadMoreCandidates = useCallback(() => {
    if (!candidatesHasMore || loadingCand || loadingMoreCand) return
    const offset = candidatesRef.current.length
    void fetchCandidatesPage(offset, CANDIDATES_PAGE_MORE, true)
  }, [candidatesHasMore, loadingCand, loadingMoreCand, fetchCandidatesPage])

  useEffect(() => {
    if (!open || !skillId) return
    setRevoking(new Set())
    setAdding(new Set())
    setSearch('')
    setDebounced('') // sync so first candidate fetch does not wait for debounce
    setCandidates([])
    setCandidatesHasMore(false)
    void loadAssigned()
  }, [open, skillId, loadAssigned])

  useEffect(() => {
    if (!open || !skillId) return
    void fetchCandidatesPage(0, CANDIDATES_INITIAL_LIMIT, false)
  }, [open, skillId, debounced, fetchCandidatesPage])

  const onScrollDrawer = useCallback(
    (e: UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget
      if (!candidatesHasMore || loadingCand || loadingMoreCand) return
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 100) {
        loadMoreCandidates()
      }
    },
    [candidatesHasMore, loadingCand, loadingMoreCand, loadMoreCandidates]
  )

  const addNewRows = useMemo(() => candidates.filter(u => !u.already_assigned), [candidates])

  const toggleRevoke = (userId: string) => {
    setRevoking(prev => {
      const n = new Set(prev)
      if (n.has(userId)) n.delete(userId)
      else n.add(userId)
      return n
    })
  }

  const toggleAdd = (userId: string) => {
    setAdding(prev => {
      const n = new Set(prev)
      if (n.has(userId)) n.delete(userId)
      else n.add(userId)
      return n
    })
  }

  const assignedChecked = (userId: string) => !revoking.has(userId)

  const saveCount = adding.size + revoking.size

  const handleSave = async () => {
    if (saveCount === 0) {
      onClose()
      return
    }
    setSaving(true)
    try {
      const addIds = [...adding]
      if (addIds.length > 0) {
        const res = await fetch(`${API_BASE}api/skills/${encodeURIComponent(skillId)}/assignments`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_ids: addIds }),
        })
        const data = (await res.json().catch(() => ({}))) as { error?: string }
        if (!res.ok) throw new Error(data.error || 'Could not assign users')
      }
      for (const uid of revoking) {
        const res = await fetch(
          `${API_BASE}api/skills/${encodeURIComponent(skillId)}/assignments/${encodeURIComponent(uid)}`,
          { method: 'DELETE', credentials: 'include' }
        )
        if (!res.ok) {
          const data = (await res.json().catch(() => ({}))) as { detail?: string }
          throw new Error(data.detail || 'Could not revoke access')
        }
      }
      toast.success('Assignments updated.')
      onSaved()
      onClose()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  if (!open) return null

  return (
    <>
      <div className="sud-overlay" aria-hidden="true" onClick={onClose} />
      <aside className="sud-drawer" role="dialog" aria-modal="true" aria-labelledby="sudTitle">
        <div className="sud-head">
          <h2 id="sudTitle" className="sud-title">
            Assign users
          </h2>
          <button type="button" className="sud-close" onClick={onClose} aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2 2L12 12M12 2L2 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="sud-tag">
          <span className="sud-tag-ico" aria-hidden="true">
            ☰
          </span>
          {skillName || 'Skill'}
        </div>

        <div className="sud-search">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.2" />
            <path d="M9 9l3.5 3.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
          </svg>
          <input
            type="search"
            placeholder="Search by name or email…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            aria-label="Search users"
          />
        </div>

        <div className="sud-scroll" onScroll={onScrollDrawer}>
          <div className="sud-section-label">Already assigned</div>
          {loading && <div className="sud-muted">Loading…</div>}
          {!loading &&
            assigned.map((a, i) => {
              const checked = assignedChecked(a.user_id)
              const label = a.username || a.email || a.user_id
              return (
                <label key={a.user_id} className={`sud-row${checked ? '' : ' muted'}`}>
                  <span className="sud-av" style={{ background: AV[i % AV.length] }}>
                    {initialsFromUser(a)}
                  </span>
                  <span className="sud-row-text">
                    <span className="sud-name">{label}</span>
                    {a.email && <span className="sud-email" title={a.email}>{a.email}</span>}
                  </span>
                  <input
                    type="checkbox"
                    className="sud-cb"
                    checked={checked}
                    onChange={() => toggleRevoke(a.user_id)}
                  />
                </label>
              )
            })}
          {!loading && assigned.length === 0 && <div className="sud-muted">No one assigned yet.</div>}

          <div className="sud-section-label sud-mt">Add new</div>
          {loadingCand && <div className="sud-muted">Searching…</div>}
          {!loadingCand &&
            addNewRows.map((u, i) => {
              const checked = adding.has(u.id)
              const label = u.username || u.email || u.id
              return (
                <label key={u.id} className={`sud-row${checked ? ' sud-row-hi' : ''}`}>
                  <span className="sud-av" style={{ background: AV[(i + 3) % AV.length] }}>
                    {initialsFromUser(u)}
                  </span>
                  <span className="sud-row-text">
                    <span className="sud-name">{label}</span>
                    {u.email && <span className="sud-email" title={u.email}>{u.email}</span>}
                  </span>
                  <input type="checkbox" className="sud-cb" checked={checked} onChange={() => toggleAdd(u.id)} />
                </label>
              )
            })}
          {loadingMoreCand && <div className="sud-muted sud-mt">Loading more…</div>}
          {!loadingCand && addNewRows.length === 0 && !loadingMoreCand && (
            <div className="sud-muted">No users match your search.</div>
          )}
        </div>

        <div className="sud-foot">
          <button type="button" className="sud-btn secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="sud-btn secondary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : saveCount > 0 ? `Save — ${saveCount} change${saveCount === 1 ? '' : 's'}` : 'Save'}
          </button>
        </div>
      </aside>
    </>
  )
}
